import { Users, Search, Loader2 } from 'lucide-react';

/**
 * Waitlist stats, search/filter bar and results table for the admin dashboard.
 * Parent component owns the data fetching and export action.
 */
const WaitlistPanel = ({
  waitlist,
  waitlistLoading,
  waitlistSearch,
  setWaitlistSearch,
  waitlistFilter,
  setWaitlistFilter,
  filteredWaitlist,
  uniqueCities,
}) => {
  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#0A0A0A] border border-zinc-800 p-6">
          <p className="font-mono text-xs text-zinc-500 tracking-widest mb-2">TOTAL SIGNUPS</p>
          <p className="font-['Josefin_Sans'] text-4xl font-bold text-[#B5FF00]">{waitlist.length}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-zinc-800 p-6">
          <p className="font-mono text-xs text-zinc-500 tracking-widest mb-2">CITIES</p>
          <p className="font-['Josefin_Sans'] text-4xl font-bold text-white">{uniqueCities.length}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-zinc-800 p-6">
          <p className="font-mono text-xs text-zinc-500 tracking-widest mb-2">LATEST SIGNUP</p>
          <p className="font-['Josefin_Sans'] text-lg font-bold text-white">
            {waitlist.length > 0 ? waitlist[waitlist.length - 1]?.name : 'N/A'}
          </p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={waitlistSearch}
            onChange={(e) => setWaitlistSearch(e.target.value)}
            className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white pl-12 pr-4 focus:border-[#B5FF00] focus:outline-none"
            data-testid="waitlist-search"
          />
        </div>
        <select
          value={waitlistFilter}
          onChange={(e) => setWaitlistFilter(e.target.value)}
          className="h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none min-w-[150px]"
          data-testid="waitlist-filter"
        >
          <option value="all">All Cities</option>
          {uniqueCities.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {waitlistLoading ? (
        <div className="text-center py-16">
          <Loader2 size={32} className="animate-spin text-[#B5FF00] mx-auto" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="waitlist-table">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">QUEUE #</th>
                <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">TYPE</th>
                <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">NAME</th>
                <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">EMAIL</th>
                <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">CITY</th>
                <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">JOINED</th>
              </tr>
            </thead>
            <tbody>
              {filteredWaitlist.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors"
                  data-testid={`waitlist-entry-${entry.queue_number}`}
                >
                  <td className="py-4 px-4">
                    <span className="font-mono text-[#B5FF00] font-bold">#{entry.queue_number}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-2 py-1 text-xs font-mono ${
                      entry.user_type === 'organiser'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'bg-zinc-800 text-zinc-400'
                    }`}>
                      {entry.user_type === 'organiser' ? 'ORGANISER' : 'USER'}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-white">{entry.name}</td>
                  <td className="py-4 px-4 text-zinc-400 font-mono text-sm">{entry.email}</td>
                  <td className="py-4 px-4">
                    <span className="bg-zinc-800 px-2 py-1 text-xs font-mono">{entry.city}</span>
                  </td>
                  <td className="py-4 px-4 text-zinc-500 font-mono text-sm">
                    {new Date(entry.joined_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredWaitlist.length === 0 && (
            <div className="text-center py-16 border border-zinc-800">
              <Users size={48} className="mx-auto text-zinc-700 mb-4" />
              <p className="text-zinc-500 font-mono">
                {waitlist.length === 0
                  ? 'No waitlist signups yet.'
                  : 'No results match your search.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WaitlistPanel;
